import type { AppChannel } from '@luminovo/apps-types';
import { parseSearch, stringifySearch, useSearch } from '@luminovo/design-system';

// The product-URL flag that asks a page for the unreleased version of its apps:
// `https://<tenant>.luminovo.com/pcb-suppliers?lumi_draft=true`.
//
// Prefixed, and the prefix is load-bearing rather than decorative. FullPageSlots
// folds every leftover search param whose key a widget declares in its
// `paramsSchema` into that widget's params, so an unprefixed `draft` is a name an
// app could plausibly declare and then receive this flag as its own input. The
// `lumi_` namespace is reserved on this platform (see WIDGET_PARENT_ORIGIN_PARAM),
// which rules that collision out by construction.
export const DRAFT_CHANNEL_PARAM = 'lumi_draft';

// `?lumi_draft` with no value counts, because that is what someone appending the
// flag to a URL by hand actually types. Anything else — a typo, a stale `=0` —
// resolves to release rather than being passed through: the platform validates the
// channel and 400s on an unknown one, and a mistyped flag should show the released
// app, not break the page.
const isDraftFlag = (value: string | undefined): boolean => value === 'true' || value === '';

export function useAppChannel(): AppChannel {
    const isDraft = useSearch({
        strict: false,
        // From the whole query string: the flag works on every page that renders apps, so no route declares it.
        select: (search) => isDraftFlag(parseSearch(stringifySearch(search))[DRAFT_CHANNEL_PARAM]),
    });
    return isDraft ? 'draft' : 'release';
}
